import DownloadButton from "../DownloadButton";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/POInvoiceLayout.css";
import "../../styles/components/transactionPrintLayouts/MRInvoiceLayout.css";

const pickValue = (source, keys, fallback = "") => {
  if (!source) return fallback;

  const normalizeKey = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const sourceKeys = Object.keys(source || {});

  const unwrapValue = (candidate) => {
    if (candidate === undefined || candidate === null) return "";
    if (typeof candidate !== "object") return candidate;

    const valueCandidate =
      candidate.value ??
      candidate.input_value ??
      candidate.label ??
      candidate.name ??
      candidate.text;

    if (
      valueCandidate !== undefined &&
      valueCandidate !== null &&
      String(valueCandidate).trim() !== ""
    ) {
      return valueCandidate;
    }

    return "";
  };

  const sourcesToCheck = [
    source,
    source.data,
    source.values,
    source.field_values,
    source.custom_fields,
    source.labels,
    source.header_labels,
    source.field_labels,
    source.display_labels,
    source.item,
    source.row,
  ].filter(Boolean);

  for (const key of keys) {
    let value = "";

    for (const candidateSource of sourcesToCheck) {
      const direct = unwrapValue(candidateSource[key]);
      if (direct !== "") {
        value = direct;
        break;
      }

      const candidateSourceKeys = Object.keys(candidateSource || {});
      const normalizedTargetKey = normalizeKey(key);
      const matchedKey = candidateSourceKeys.find(
        (sourceKey) => normalizeKey(sourceKey) === normalizedTargetKey
      );

      if (matchedKey) {
        const matchedValue = unwrapValue(candidateSource[matchedKey]);
        if (matchedValue !== "") {
          value = matchedValue;
          break;
        }
      }
    }

    if (value === "") {
      const normalizedTargetKey = normalizeKey(key);
      const matchedKey = sourceKeys.find(
        (sourceKey) => normalizeKey(sourceKey) === normalizedTargetKey
      );
      if (matchedKey) {
        value = unwrapValue(source[matchedKey]);
      }
    }

    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return fallback;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";

  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return String(dateStr);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const normalizeKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const toTitle = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getFieldOrderWeight = (field) => {
  const normalized = `${normalizeKey(field?.field_name)} ${normalizeKey(field?.title)}`;

  if (normalized.includes("description")) return 1;
  if (normalized.includes("specification") || normalized.includes("spec")) return 2;
  if (normalized.includes("qty") || normalized.includes("quantity")) return 3;
  if (normalized.includes("uom") || normalized.includes("unit")) return 4;
  if (normalized.includes("made") || normalized.includes("make")) return 5;

  return 100;
};

export const PurchaseIndentInvoiceLayout = ({
  headDetails = {},
  companyData = {},
  itemDetails = [],
  rowData = [],
  invoiceTableData,
  selectedReportName,
}) => {
  let organizationData = {};
  try {
    organizationData = JSON.parse(localStorage.getItem("organisation") || "{}");
  } catch (error) {
    organizationData = {};
  }

  const backendFields = (Array.isArray(rowData) && rowData.length
    ? rowData.filter((field) => !field.is_internal_field && field.field_name)
    : Object.keys((Array.isArray(itemDetails) && itemDetails[0]) || {}).map((key) => ({
        field_name: key,
        title: toTitle(key),
      }))
  )
    .map((field) => ({
      field_name: field.field_name,
      title: field.title || field.display_name || toTitle(field.field_name),
    }))
    .filter((field) => {
      const normalized = `${normalizeKey(field.field_name)} ${normalizeKey(field.title)}`;
      return !(normalized.includes("required") && normalized.includes("date")) &&
        !normalized.includes("remark") &&
        !normalized.includes("narration");
    });

  const extraFields = [
    { field_name: "material_required_date", title: "MATERIAL REQUIRED DATE" },
    { field_name: "remarks", title: "REMARKS" },
  ];

  const orderedFieldMap = new Map();
  [...backendFields, ...extraFields].forEach((field) => {
    if (!field?.field_name || orderedFieldMap.has(field.field_name)) return;
    orderedFieldMap.set(field.field_name, {
      field_name: field.field_name,
      title: field.title || field.display_name || toTitle(field.field_name),
    });
  });

  const orderedFields = [...orderedFieldMap.values()].sort((a, b) => {
    const weightDiff = getFieldOrderWeight(a) - getFieldOrderWeight(b);
    if (weightDiff !== 0) return weightDiff;
    return String(a.title || a.field_name || "").localeCompare(
      String(b.title || b.field_name || "")
    );
  });

  const requestDate = formatDate(
    pickValue(headDetails, ["date", "created_at", "request_date", "tran_date", "requested_date"])
  );

  const saleWorkOrder = pickValue(headDetails, [
    "sale_work_order",
    "sales_work_order",
    "wo_no",
    "work_order",
    "work_order_no",
    "ref_tran_no",
    "reference_no",
    "tran_no",
  ]);

  const customerName = pickValue(
    headDetails,
    ["customer_name", "party_name", "company_name", "customer"],
    companyData.company_name || ""
  );

  const formatNo = pickValue(headDetails, ["format_no", "form_no", "document_no"]);
  const versionNo = pickValue(headDetails, ["ver_no", "version_no", "revision_no"]);
  const versionDate = formatDate(
    pickValue(headDetails, ["ver_date", "version_date", "revision_date"])
  );

  const purchaseIndentTitle = pickValue(
    headDetails,
    [
      "form_title",
      "report_title",
      "title",
      "transaction_name",
      "tran_name",
      "purchase_indent_title",
      "indent_title",
    ],
    selectedReportName || "PURCHASE INDENT FORM"
  );

  const formatNoLabel = pickValue(headDetails, ["format_no_label"], "FORMAT NO");
  const versionNoLabel = pickValue(headDetails, ["ver_no_label", "version_no_label"], "VER NO");
  const versionDateLabel = pickValue(
    headDetails,
    ["ver_date_label", "version_date_label"],
    "VER DATE"
  );
  const saleWorkOrderLabel = pickValue(
    headDetails,
    ["sale_work_order_label", "work_order_label", "work_order_no_label"],
    "WORK ORDER NO"
  );
  const customerNameLabel = pickValue(
    headDetails,
    ["customer_name_label", "party_name_label", "company_name_label"],
    "CUSTOMER NAME"
  );
  const requestDateLabel = pickValue(
    headDetails,
    ["request_date_label", "date_label", "tran_date_label"],
    "REQUEST DATE"
  );
  const slNoLabel = pickValue(headDetails, ["sl_no_label", "serial_label"], "SL.NO");

  const rows = Array.isArray(itemDetails)
    ? itemDetails.map((item, index) => ({
        slNo: index + 1,
        values: orderedFields.reduce((acc, field) => {
          acc[field.field_name] = pickValue(item, [field.field_name], "");
          return acc;
        }, {}),
      }))
    : [];

  const piDownloadRows = rows.map((row) =>
    orderedFields.reduce(
      (acc, field) => {
        acc.sl_no = row.slNo;
        acc[field.field_name] = row.values?.[field.field_name] || "";
        return acc;
      },
      {}
    )
  );

  const piTableData = {
    headings: [
      { display_name: "SL.NO", field_name: "sl_no", is_internal: false },
      ...orderedFields.map((field) => ({
        display_name: field.title || toTitle(field.field_name),
        field_name: field.field_name,
        is_internal: false,
      })),
    ],
    rows: piDownloadRows,
  };

  const formatCompanyAddress = (data) => {
    if (!data) return "N/A";
    const parts = [data.address?.trim(), data.city?.trim()].filter(Boolean);
    const pin = data.pincode ? `- ${data.pincode}` : "";
    return parts.length > 0 || pin
      ? `${parts.join(", ")} ${pin}`.trim()
      : "N/A";
  };

  return (
    <div className="invoice-container workorder-layout mr-workorder-layout mr-page-container">
      <h2 className="invoice-title" style={{ textAlign: "center" }}>
        {purchaseIndentTitle}
      </h2>

      <div className="invoice-box">
        <div className="invoice-left">
          <div className="invoice-logo-container">
            <img
              src={organizationData.logo_url || ProfilePlaceholder}
              alt="Logo"
              className="invoice-logo-content"
            />
          </div>
          <div>
            <h3 className="invoice-company-name">
              {organizationData.name || "N/A"}
            </h3>
            <p className="invoice-company-address">
              {formatCompanyAddress(organizationData)}
              <br />
              GSTIN: {organizationData.gst_no || "N/A"}
              <br />
              State: {organizationData.state || "N/A"}
            </p>
          </div>
        </div>

        <div className="invoice-right">
          {[
            { label: formatNoLabel, value: formatNo || "" },
            { label: versionNoLabel, value: versionNo || "" },
            { label: versionDateLabel, value: versionDate || "" },
          ].map((item, index) => (
            <div key={index} className="purchase-detail">
              <p className="detail-label">
                <strong>{item.label}:</strong>
              </p>
              <div className="detail-value">{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div
        className="workorder-header-row workorder-meta-row"
        style={{
          borderLeft: "1px solid #9ca3af",
          borderRight: "1px solid #9ca3af",
          gridTemplateColumns: "1.15fr 2.25fr 1fr",
        }}
      >
        <div className="workorder-meta-cell">
          <span className="workorder-meta-label">{saleWorkOrderLabel}:</span>
          <span className="workorder-meta-value" style={{ whiteSpace: "nowrap" }}>
            {saleWorkOrder || "-"}
          </span>
        </div>
        <div className="workorder-meta-cell">
          <span className="workorder-meta-label">{customerNameLabel}:</span>
          <span className="workorder-meta-value">{customerName || "-"}</span>
        </div>
        <div className="workorder-meta-cell">
          <span className="workorder-meta-label">{requestDateLabel}:</span>
          <span className="workorder-meta-value workorder-date-value">
            {requestDate || "-"}
          </span>
        </div>
      </div>

      <div className="invoice-overflow-x-scroll mr-table-scroll">
        <table className="invoice-table mr-table-workorder">
          <thead className="invoice-thead">
            <tr>
              <th className="invoice-th mr-col-sl" style={{ borderLeftWidth: "0px" }}>
                {slNoLabel}
              </th>
              {orderedFields.map((field, index) => (
                <th
                  key={field.field_name}
                  className="invoice-th"
                  style={
                    index === orderedFields.length - 1
                      ? { borderRightWidth: "0px" }
                      : {}
                  }
                >
                  {field.title || toTitle(field.field_name)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.slNo}>
                <td className="invoice-td mr-col-sl" style={{ borderLeftWidth: "0px" }}>
                  {row.slNo}
                </td>
                {orderedFields.map((field, idx) => (
                  <td
                    key={`${row.slNo}-${field.field_name}`}
                    className="invoice-td"
                    style={
                      idx === orderedFields.length - 1
                        ? { borderRightWidth: "0px" }
                        : {}
                    }
                  >
                    {row.values?.[field.field_name] || ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="invoice-system-generated-note">
        This is a system-generated document and does not require a signature.
      </p>

      <div className="invoice-buttons mr-actions">
        <DownloadButton
          tableData={invoiceTableData || piTableData}
          fileName={selectedReportName || "purchase-indent"}
          downloadFormats={["excel", "pdf", "full-page-pdf"]}
          fullPageDownload={true}
          fullPageSelector=".mr-page-container"
        />
      </div>
    </div>
  );
};
